import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { getContractLightById, searchContracts } from 'app/api/contractApi';
import { useNavigate } from 'react-router-dom';

interface ContractProps {
  id?: any;
  value?: number;
  onContractChanged: any;
  isDeletable?: boolean;
  readonly?: boolean;
}

const ContractWidget = ({ id, value, onContractChanged, isDeletable, readonly }: ContractProps) => {
  const navigate = useNavigate();

  const [selectedPerson, setSelectedPerson] = useState(null);
  const [persons, setPersons] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<string>('');
  const [visibleSearchDialog, setVisibleSearchDialog] = useState(false);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      if (value) {
        getContractLightById(value).then(response => {
          // setPersons([{ label: buildLabel(response.data), value: response.data.id }]);
          setSelectedPerson(response.data);
        });
      }
    }
  }, [firstLoad]);

  const fetchData = async (pageNumber: number, filterValue: string = '') => {
    setLoading(true);
    try {
      let params = '';
      if (filterValue) {
        params = '?query=' + filterValue;
      }

      const response = await searchContracts(params);

      setPersons(response.data.content); // Reset options on new filter
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter) {
      const delayDebounceFn = setTimeout(() => {
        fetchData(0, filter);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filter]);

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setVisibleSearchDialog(false)}
      />
    </div>
  );

  const handleSelectPerson = (rowData: any) => {
    setSelectedPerson(rowData);
    onContractChanged(rowData.id);
    setVisibleSearchDialog(false); // Close the modal after selection
  };

  // Template for the "Select" button in each row
  const selectButtonTemplate = (rowData: any) => (
    <Button label="Vybrať" style={{ backgroundColor: '#e31818' }} onClick={() => handleSelectPerson(rowData)} />
  );

  const onOpenSearchModal = () => {
    setFilter('');
    setPersons([]);
    setVisibleSearchDialog(true);
  };

  const onDeleteAgent = () => {
    setSelectedPerson(null);
    onContractChanged(null);
  };

  return (
    <div className="p-inputgroup flex-1">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          width: '100%',
        }}
      >
        {/* Left section with text info */}
        <div onClick={!readonly ? onOpenSearchModal : null}>
          {selectedPerson?.policyHolder?.businessName && (
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{selectedPerson?.policyHolder?.businessName}</div>
          )}
          {!selectedPerson?.policyHolder?.businessName && (
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {selectedPerson?.policyHolder?.firstName} {selectedPerson?.policyHolder?.lastName}
            </div>
          )}
          <div style={{ margin: '5px 0' }}>
            {selectedPerson?.product?.name}{' '}
            {selectedPerson?.product?.insuranceCompany?.name && '(' + selectedPerson?.product?.insuranceCompany?.name + ')'}
          </div>
          <div style={{ margin: '5px 0' }}>{selectedPerson?.contractNr}</div>
          <div style={{ margin: '5px 0' }}>{selectedPerson?.proposalNr}</div>
        </div>
        <div style={{ display: 'flex' }}>
          <Divider layout="vertical" />
          <div>
            <div>Platba: {selectedPerson?.payment} EUR</div>
            <div>Frekvencia: {selectedPerson?.paymentFrequency?.name}</div>
          </div>
        </div>

        {/* Right section with button */}
        {!readonly && selectedPerson?.id && (
          <div style={{ display: 'flex' }}>
            <Divider layout="vertical" />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <Button
                label="Nájsť"
                type="button"
                onClick={onOpenSearchModal}
                size="small"
                icon="pi pi-search"
                style={{ backgroundColor: '#e31818', height: '30px', marginBottom: '5px' }}
              />
              <Button
                label="Prejsť do zmluvy"
                type="button"
                onClick={() => window.open(`/create-contract/${value}`, '_blank', 'noopener,noreferrer')}
                size="small"
                icon="pi pi-file-edit"
                style={{ backgroundColor: '#e31818', height: '30px', marginBottom: '5px' }}
              />
              {isDeletable && (
                <Button
                  label="Zrušiť"
                  type="button"
                  onClick={onDeleteAgent}
                  size="small"
                  severity="danger"
                  icon="pi pi-info-circle"
                  style={{ height: '30px' }}
                />
              )}
            </div>
          </div>
        )}
        {!readonly && !selectedPerson?.id && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              style={{ backgroundColor: '#e31818' }}
              label="Nájsť zmluvu"
              type="button"
              onClick={onOpenSearchModal}
              size="small"
              icon="pi pi-search"
            />
          </div>
        )}
      </div>

      <Dialog
        visible={visibleSearchDialog}
        header="Nájsť zmluvu"
        style={{ width: '80vw' }}
        footer={dialogFooter}
        onHide={() => setVisibleSearchDialog(false)}
      >
        {/* InputText for user query */}
        <div className="p-field col-12" style={{ width: '100%' }}>
          <InputText
            style={{ width: '100%' }}
            id="query"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Hľadať zmluvu"
          />
        </div>

        {/* DataTable to show results */}
        {persons.length > 0 && (
          <DataTable stripedRows loading={loading} size="small" lazy value={persons} responsiveLayout="scroll" className="p-mt-4">
            <Column field="contractNr" header="Číslo zmluvy" />
            <Column field="proposalNr" header="Číslo návrhu" />
            <Column field="product.name" header="Produkt" />
            <Column field="product.insuranceCompany.name" header="Spoločnosť" />
            <Column field="policyHolder.firstName" header="Prvé meno" />
            <Column field="policyHolder.lastName" header="Priezvisko" />
            <Column field="policyHolder.businessName" header="Názov" />
            <Column body={selectButtonTemplate} header="Akcia" />
          </DataTable>
        )}

        {/*<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>*/}
        {/*  <Button label="Vytvoriť novú osobu" type="button" onClick={() => onOpenCreateModal(null)} size="large" />*/}
        {/*</div>*/}
      </Dialog>
    </div>
  );
};

export default ContractWidget;
